import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Button, 
  Input, 
  Select, 
  Space, 
  Tag, 
  Drawer, 
  message,
  Typography,
  Avatar
} from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../components/layout/PageLayout';
import ViewBanner from '../../components/banners/ViewBanner';
import bannerService from '../../services/bannerService';
import { useUser } from '../../context/UserContext';

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const BannerList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [bannerList, setBannerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewVisible, setViewVisible] = useState(false);
  const [viewObject, setViewObject] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });
  
  // Filters state
  const [filters, setFilters] = useState({
    searchText: '',
    status: undefined
  });

  // Table columns configuration
  const bannerColumns = [
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: 80,
      render: (text) => text === null || text === undefined ? 1 : text
    },
    {
      title: 'Banner Name',
      dataIndex: 'banner_name',
      key: 'banner_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.banner_name.localeCompare(b.banner_name)
    },
    {
      title: 'Show Type',
      dataIndex: 'show_type',
      key: 'show_type',
      render: (text) => (
        <Tag color={text === 'movie' ? 'blue' : 'green'}>
          {text === 'movie' ? 'Movie' : 'TV Show'}
        </Tag>
      )
    },
    {
      title: 'Show Name',
      key: 'showName',
      render: (text, record) => (
        <Text>
          {record.show_type === 'movie' 
            ? record.video_show_title 
            : record.tv_show_name}
        </Text>
      )
    },
    {
      title: 'Banner Page',
      key: 'bannerPage',
      render: (text, record) => {
        const pages = [];
        if (record.home_page === 'Y') pages.push('Home');
        if (record.movie_page === 'Y') pages.push('Movie');
        if (record.series_page === 'Y') pages.push('Series');
        
        return (
          <Space>
            {pages.map((page, index) => (
              <Tag key={index} color="cyan">{page}</Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'banner_is_active',
      key: 'banner_is_active',
      defaultSortOrder: 'ascend',
      render: (text) => (
        <Tag color={text === 'Y' ? 'green' : 'red'}>
          {text === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (text, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            size="small"
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
        </Space>
      )
    }
  ];

  // Fetch banners data
  const getBanners = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        perPage: pagination.pageSize,
        currentPage: pagination.current
      };
      
      const response = await bannerService.getBanners(params);
      const { data } = response;
      
      if (data && data.status) {
        setBannerList(data.data.data || []);
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination?.total || 0,
          current: data.data.pagination?.currentPage || 1
        }));
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      message.error('Failed to fetch banners');
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    getBanners();
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      searchText: '',
      status: undefined
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle table change (pagination, sorting)
  const handleTableChange = (newPagination, filters, sorter) => {
    if (
      newPagination.current !== pagination.current ||
      newPagination.pageSize !== pagination.pageSize
    ) {
      setPagination(prev => ({
        ...prev,
        current: newPagination.current,
        pageSize: newPagination.pageSize
      }));
    }
  };

  // Handle view banner
  const handleView = (record) => {
    setViewObject(record);
    setViewVisible(true);
  };

  // Handle edit banner
  const handleEdit = (record) => {
    navigate(`/banners/edit/${record.m_banner_id}`);
  };

  // Handle add banner
  const handleAddBanner = () => {
    navigate('/banners/addbanner');
  };

  // Close view drawer
  const handleCloseView = () => {
    setViewVisible(false);
    setViewObject(null);
  };

  // Load data on component mount and when filters/pagination change
  useEffect(() => {
    getBanners();
  }, [pagination.current, pagination.pageSize, filters.searchText, filters.status]);

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Banner Management</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Banner Management"
      desc="Manage banners for your OTT platform"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        {/* Table Operations */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddBanner}
            >
              Add Banner
            </Button>
            
            <Select
              placeholder="Status"
              style={{ width: 150 }}
              value={filters.status}
              onChange={handleStatusChange}
              allowClear
            >
              <Option value="Y">Active</Option>
              <Option value="N">Inactive</Option>
            </Select>
            
            <Search
              placeholder="Search banners..."
              style={{ width: 200 }}
              value={filters.searchText}
              onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
              onSearch={handleSearch}
              allowClear
            />
            
            <Button type="primary" onClick={handleApplyFilters}>
              Apply
            </Button>
            
            <Button onClick={handleResetFilters}>
              Reset
            </Button>
          </div>
        </div>

        {/* Data Table */}
        <Table
          columns={bannerColumns}
          dataSource={bannerList}
          rowKey="m_banner_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
        />
      </Card>

      {/* View Banner Drawer */}
      <Drawer
        title="Banner Details"
        width={640}
        placement="right"
        closable={false}
        open={viewVisible}
        onClose={handleCloseView}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button type="primary" onClick={handleCloseView}>
              Close
            </Button>
          </div>
        }
      >
        <ViewBanner viewdata={viewObject}>
          <Button 
            type="primary" 
            onClick={handleCloseView}
            style={{ marginTop: '15px' }}
          >
            Close
          </Button>
        </ViewBanner>
      </Drawer>
    </PageLayout>
  );
};

export default BannerList;